import { useCallback, useEffect, useState } from 'react';
import { useQuery, useMutation } from 'react-apollo';
import { useDispatch, useSelector } from 'react-redux';

import { checkoutPrice as checkoutPriceQuery } from '../../../../graphql/queries/checkout';
import { checkout as checkoutMutation } from '../../../../graphql/mutations/checkout';
import getGlobalAccountCardQuery from '../../../../graphql/queries/getGlobalAccountCard';
import {
  accountCardConfigurationValidate,
  mapAccountCardToCheckoutInput,
} from './helpers';
import { getErrorString } from '../../../../util/normalize-errors';
import { openAlert } from '../../../../actions/modal-actions';
import { sentryCaptureMessage } from '../../../../util/sentry';

/**
 * AccountCard checkout hook.
 *
 * @param {Object} options The options.
 * @param {Object} options.accountCard The AccountCard.
 * @param {function} [options.onCheckoutError] Error callback.
 * @param {function} [options.onCheckoutSuccess] Success callback.
 * @returns {{onCheckout: (function(*=): Promise<undefined>), checkoutError: undefined, canCheckout: boolean, checkingOut: boolean, onCheckoutReset: onCheckoutReset, checkoutSuccess: undefined, loadingPricing: boolean, pricing: *}}
 */
const useAccountCardCheckout = ({
  accountCard,
  onCheckoutError,
  onCheckoutSuccess,
}) => {
  // TODO [cards] add verification method.
  const [promoCode, setPromoCode] = useState('');
  const [paymentSummaryOpen, setPaymentSummaryOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState();
  const [checkoutSuccess, setCheckoutSuccess] = useState();

  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const dispatch = useDispatch();
  const onErrorModalOpen = useCallback(state => dispatch(openAlert(state)), [
    dispatch,
  ]);

  const { data: { globalAccountCard } = {} } = useQuery(
    getGlobalAccountCardQuery,
  );
  const accountCardCheckoutInput = mapAccountCardToCheckoutInput({
    accountCard,
    globalAccountCard,
    promoCode,
  });

  const canCheckout =
    accountCardConfigurationValidate(accountCard) && !!globalAccountCard;

  // Pricing
  //
  const {
    data: { checkoutPrice } = {},
    loading: loadingPricing,
    refetch: refetchPricing,
  } = useQuery(checkoutPriceQuery, {
    variables: {
      ...accountCardCheckoutInput,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: !accountCardCheckoutInput || !authenticated || !canCheckout,
  });

  useEffect(() => {
    if (accountCard && authenticated && canCheckout) {
      refetchPricing?.();
    }
  }, [accountCard, authenticated]);

  // Checkout
  //
  const [checkout] = useMutation(checkoutMutation);
  const onCheckout = async token => {
    if (checkingOut) {
      return;
    }

    setCheckingOut(true);
    setCheckoutError();
    setCheckoutSuccess(false);

    try {
      await checkout({
        variables: {
          input: {
            stripe_token: token?.id,
            email_address: token?.email,
            platform: 'WEB',
            stripe_payment_method_id: token?.stripe_payment_method_id,
            stripe_connect_account_id: token?.stripe_connect_account_id,
            ...accountCardCheckoutInput,
            promo_code:
              token?.promo_code ?? accountCardCheckoutInput?.promo_code,
          },
        },
      });

      setPaymentSummaryOpen(false);
      setCheckoutSuccess(true);
      onCheckoutSuccess?.({ accountCard });
    } catch (responseError) {
      sentryCaptureMessage(
        responseError?.message ??
          '[AccountCard Checkout] :: Error checking out',
        {
          level: 'error',
          error: responseError,
        },
      );

      setCheckoutError(getErrorString(responseError));

      if (onCheckoutError) {
        onCheckoutError(responseError);
      } else {
        onErrorModalOpen({
          message:
            'There was an error during checkout, I have alerted my best people! Please try again in a while.',
          info: responseError?.request_id || getErrorString(responseError),
        });
      }
    } finally {
      setCheckingOut(false);
    }
  };

  const onCheckoutReset = () => {
    setCheckoutSuccess(false);
    setCheckingOut(false);
    setCheckoutError();
    setPromoCode('');
  };

  return {
    canCheckout,

    accountCardCheckoutInput,
    pricing: checkoutPrice,
    loadingPricing,

    promoCode,
    setPromoCode,

    paymentSummaryOpen,
    setPaymentSummaryOpen,

    globalAccountCard,

    onCheckout,
    checkoutError,
    checkoutSuccess,
    checkingOut,
    onCheckoutReset,
  };
};

export default useAccountCardCheckout;
